import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, Film, Tv, Star, Sparkles } from 'lucide-react';
import { Media, Movie, TVSeries } from '../types';
import { getTrending, getPopularMovies, getPopularTVSeries } from '../api/tmdb';
import MovieCard from '../components/MovieCard';
import SearchBar from '../components/SearchBar';

const Home: React.FC = () => {
  // State for different sections
  const [trending, setTrending] = useState<Media[]>([]);
  const [popularMovies, setPopularMovies] = useState<Movie[]>([]);
  const [popularTVSeries, setPopularTVSeries] = useState<TVSeries[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load data on component mount
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch all data in parallel
        const [trendingData, moviesData, tvData] = await Promise.all([
          getTrending('week'),
          getPopularMovies(1),
          getPopularTVSeries(1)
        ]);

        setTrending(trendingData.results);
        setPopularMovies(moviesData.results);
        setPopularTVSeries(tvData.results);
      } catch (err) {
        console.error('Error loading home data:', err);
        setError('Failed to load content. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // Loading skeleton component
  const LoadingSkeleton = () => (
    <div className="grid-modern">
      {Array.from({ length: 12 }).map((_, index) => (
        <div key={index} className="animate-pulse">
          <div className="aspect-[2/3] bg-gray-200 dark:bg-dark-700 rounded-2xl mb-3 shimmer"></div>
          <div className="h-4 bg-gray-200 dark:bg-dark-700 rounded mb-2 shimmer"></div>
          <div className="h-3 bg-gray-200 dark:bg-dark-700 rounded w-2/3 shimmer"></div>
        </div>
      ))}
    </div>
  );

  // Error component
  const ErrorMessage = ({ message }: { message: string }) => (
    <div className="text-center py-16">
      <div className="text-red-500 text-lg mb-6">{message}</div>
      <button
        onClick={() => window.location.reload()}
        className="btn-modern"
      >
        Try Again
      </button>
    </div>
  );

  // Section component
  const Section = ({ 
    title, 
    icon: Icon, 
    children, 
    className = '' 
  }: { 
    title: string; 
    icon: React.ComponentType<{ className?: string }>; 
    children: React.ReactNode; 
    className?: string;
  }) => (
    <section className={`mb-16 ${className}`}>
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        className="flex items-center space-x-3 mb-8"
      >
        <div className="p-2 rounded-lg bg-gradient-to-r from-primary-500 to-primary-600">
          <Icon className="h-5 w-5 text-white" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">{title}</h2>
      </motion.div>
      {children}
    </section>
  );

  if (loading) {
    return (
      <div className="container-modern py-8">
        <Section title="Loading..." icon={TrendingUp}>
          <LoadingSkeleton />
        </Section>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container-modern py-8">
        <ErrorMessage message={error} />
      </div>
    );
  }

  return (
    <div className="container-modern py-8">
      {/* Hero Section with Search */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center py-12 mb-16"
      >
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
            className="flex items-center justify-center space-x-2 mb-6"
          >
            <Sparkles className="h-8 w-8 text-gradient" />
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 dark:text-white">
              Discover Amazing
              <span className="text-gradient block"> Movies & Series</span>
            </h1>
            <Sparkles className="h-8 w-8 text-gradient" />
          </motion.div>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-xl text-gray-600 dark:text-gray-400 mb-10 max-w-2xl mx-auto leading-relaxed"
          >
            Explore the latest trending movies and TV shows. Find your next favorite entertainment with our powerful search and discovery tools.
          </motion.p>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="max-w-2xl mx-auto"
          >
            <SearchBar showFilters={false} />
          </motion.div>
        </div>
      </motion.div>

      {/* Trending Section */}
      <Section title="Trending This Week" icon={TrendingUp}>
        <div className="grid-modern">
          {trending.slice(0, 12).map((item, index) => (
            <motion.div
              key={`${item.media_type}-${item.id}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <MovieCard item={item} />
            </motion.div>
          ))}
        </div>
      </Section>

      {/* Popular Movies Section */}
      <Section title="Popular Movies" icon={Film}>
        <div className="grid-modern">
          {popularMovies.slice(0, 12).map((movie, index) => (
            <motion.div
              key={`movie-${movie.id}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <MovieCard item={movie} showType={false} />
            </motion.div>
          ))}
        </div>
      </Section>

      {/* Popular TV Series Section */}
      <Section title="Popular TV Series" icon={Tv}>
        <div className="grid-modern">
          {popularTVSeries.slice(0, 12).map((series, index) => (
            <motion.div
              key={`tv-${series.id}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <MovieCard item={series} showType={false} />
            </motion.div>
          ))}
        </div>
      </Section>

      {/* Call to Action */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center py-16 bg-gradient-to-r from-primary-50 to-primary-100 dark:from-dark-800 dark:to-dark-700 rounded-3xl"
      >
        <Star className="h-16 w-16 text-gradient mx-auto mb-6" />
        <h3 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
          Can't find what you're looking for?
        </h3>
        <p className="text-lg text-gray-600 dark:text-gray-400 mb-8 max-w-2xl mx-auto">
          Use our advanced search to discover more movies and TV series. Filter by genre, year, rating, and more.
        </p>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => window.location.href = '/search'}
          className="btn-modern px-8 py-4 text-lg"
        >
          Explore More
        </motion.button>
      </motion.div>
    </div>
  );
};

export default Home; 